import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import { useNavigate } from "react-router-dom";
import DateTimeHeader from "../components/DateTimeHeader";
import Dropdown from "../components/Dropdown";
import {
  Shield,
  Users,
  Settings,
  BarChart3,
  Database,
  UserCheck,
  Activity,
  AlertTriangle,
  Server,
  Monitor,
  Lock,
  Key,
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  Eye,
  UserX,
  Bell,
  RefreshCw,
  Moon,
  Sun,
  CalendarCheck,
  Clock,
  Video,
  AlertCircle,
  X,
} from "lucide-react";
import UserManagement from "../components/admin/UserManagement";
import { useTheme } from "../contexts/ThemeContext";
import NavBar from "../components/NavBar";
import { useAdmin } from "../contexts/adminContext";

const AdminPortal: React.FC = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "overview" | "users" | "meetings" | "settings" | "directory" | "system"
  >("overview");
  // const [darkMode, setDarkMode] = useState(false);

  const { stats: adminStats, loading, error, refreshStats, recentMeetings: recentMeetingsStats, fetchRecentMeetings, topUsers: topUsersStats, meetingAnalytics, topMeetingRooms, fetchMeetingAnalytics, generalSettings, updateGeneralSettings } = useAdmin();

  // console.log("topUsersStats", topUsersStats);
const timeToMinutes = (timeString: string) => {
  if (!timeString) return 0;
  const [h, m, s] = timeString.split(":").map(Number);
  return h * 60 + m + (s ? s / 60 : 0);
};

const avgMeetingDuration = React.useMemo(() => {
  if (!recentMeetingsStats || recentMeetingsStats.length === 0) return 0;

  let totalMinutes = 0;

  recentMeetingsStats.forEach((m) => {
    const start = timeToMinutes(m.start_time);
    const end = timeToMinutes(m.end_time);

    if (end > start) {
      totalMinutes += end - start;
    }
  });
const avg = totalMinutes / recentMeetingsStats.length; 
return (avg / 60).toFixed(1);   // hours with 1 decimal
}, [recentMeetingsStats]);


  const stats = [
    {
      label: "Total Users",
      value: adminStats?.totalUsers || 0,
      icon: <Users size={24} />,
      color: "bg-gradient-to-r from-red-600 to-pink-600",
    },
    {
      label: "Active Meetings",
      value: adminStats?.activeMeetings || 0,
      icon: <Video size={24} />,
      color: "bg-gradient-to-r from-red-600 to-pink-600",
    },
    {
      label: "Completed Meetings",
      value: adminStats?.completedMeetings || 0,
      icon: <CalendarCheck size={24} />,
      color: "bg-gradient-to-r from-red-600 to-pink-600",
    },
    {
      label: "Average Duration",
      value: avgMeetingDuration + " hrs",
      icon: <Clock size={24} />,
      color: "bg-gradient-to-r from-red-600 to-pink-600",
    },
  ];

  const initialNotifications = [
    {
      id: 1,
      icon: <CalendarCheck className="text-blue-400" size={18} />,
      text: "Team Standup meeting starts in 15 minutes",
    },
    {
      id: 2,
      icon: <Video className="text-green-400" size={18} />,
      text: 'Sarah invited you to join "Q4 Planning Session"',
    },
    {
      id: 3,
      icon: <Users className="text-purple-400" size={18} />,
      text: "3 participants joined your meeting room",
    },
    {
      id: 4,
      icon: <AlertCircle className="text-red-400" size={18} />,
      text: "Your account password expires in 7 days",
    },
  ];

  const [notifications, setNotifications] = useState(initialNotifications);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [clearedNotifications, setClearedNotifications] = useState<any[]>([]);
  const [notificationsTab, setNotificationsTab] = useState<
    "current" | "cleared"
  >("current");

  // System Settings state
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    enforceTwoFactor: false,
    passwordExpiryDays: 90,
    sessionTimeoutMinutes: 120,
    enablePasswordExpiry: true,
    enableSessionTimeout: true,
    enableMeetingRoomTimeout: false,
    meetingRoomTimeoutMinutes: 60,
    enableEmailNotifications: true,
    enableSystemAlerts: true,
    backupFrequencyHours: 24,
    allowGuestAccess: false,
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const handleSystemSettingToggle = (key: keyof typeof systemSettings) => {
    setSystemSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const handleSystemSettingChange = (
    key: keyof typeof systemSettings,
    value: number | string
  ) => {
    setSystemSettings((prev) => ({ ...prev, [key]: value }));
  };
  const handleSaveSystemSettings = async () => {
    setIsSavingSettings(true);
    try {
      await updateGeneralSettings({
        session_timeout_minutes: systemSettings.sessionTimeoutMinutes,
        password_expiration_days: systemSettings.passwordExpiryDays,
        enforce_two_factor: systemSettings.enforceTwoFactor,
        enable_email_notifications: systemSettings.enableEmailNotifications,
        enable_system_alerts: systemSettings.enableSystemAlerts,
        allow_guest_access: systemSettings.allowGuestAccess,
        is_session_expiry_enabled: systemSettings.enableSessionTimeout,
        is_password_expiry_enabled: systemSettings.enablePasswordExpiry,
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  
  // Platform Announcement state
  type AnnouncementSeverity = "info" | "warning" | "critical";
  type AnnouncementAudience = "all" | "admins" | "standard";
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementMessage, setAnnouncementMessage] = useState("");
  const [announcementSeverity, setAnnouncementSeverity] =
    useState<AnnouncementSeverity>("info");
  const [announcementAudience, setAnnouncementAudience] =
    useState<AnnouncementAudience>("all");
  const [announcementExpiry, setAnnouncementExpiry] = useState<string>("");
  const [isSendingAnnouncement, setIsSendingAnnouncement] = useState(false);
  const { darkMode } = useTheme();
  const sendPlatformAnnouncement = async () => {
    if (!announcementMessage.trim()) return;
    setIsSendingAnnouncement(true);
    try {
      await updateGeneralSettings({
        announcement_title: announcementTitle.trim() || null,
        announcement_message: announcementMessage.trim(),
        announcement_severity: announcementSeverity,
        announcement_audience: announcementAudience,
        announcement_expires_at: announcementExpiry || null,
      });
    } catch { }
    const color =
      announcementSeverity === "critical"
        ? "text-red-500"
        : announcementSeverity === "warning"
          ? "text-yellow-500"
          : "text-blue-500";
    const prefix = announcementTitle.trim()
      ? `${announcementTitle.trim()}: `
      : "";
    setNotifications((prev) => [
      {
        id: Date.now(),
        icon: <AlertCircle className={color} size={18} />,
        text: `${prefix}${announcementMessage.trim()}${announcementExpiry
            ? " (expires " + new Date(announcementExpiry).toLocaleString() + ")"
            : ""
          }`,
      },
      ...prev,
    ]);
    setIsSendingAnnouncement(false);
    setAnnouncementTitle("");
    setAnnouncementMessage("");
    setAnnouncementSeverity("info");
    setAnnouncementAudience("all");
    setAnnouncementExpiry("");
  };

  React.useEffect(() => {
    if (generalSettings) {
      setSystemSettings((prev) => ({
        ...prev,
        enforceTwoFactor: !!generalSettings.enforce_two_factor,
        passwordExpiryDays: generalSettings.password_expiration_days ?? prev.passwordExpiryDays,
        sessionTimeoutMinutes: generalSettings.session_timeout_minutes ?? prev.sessionTimeoutMinutes,
        enableEmailNotifications: !!generalSettings.enable_email_notifications,
        enableSystemAlerts: !!generalSettings.enable_system_alerts,
        allowGuestAccess: !!generalSettings.allow_guest_access,
        enableSessionTimeout: !!generalSettings.is_session_expiry_enabled,
        enablePasswordExpiry: !!generalSettings.is_password_expiry_enabled,
      }));
    }
  }, [generalSettings]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://vela-back.wingtrill.com';

  // SSO Config state
  const [ssoConfig, setSsoConfig] = useState({
    enabled: false,
    enforce_sso: false,
    issuer_url: '',
    client_id: '',
    client_secret: '',
  });
  const [isSavingSso, setIsSavingSso] = useState(false);
  const [ssoSaveMsg, setSsoSaveMsg] = useState<string | null>(null);

  // SSO Audit Logs state
  type AuditLog = { id: number; email?: string; provider?: string; status: string; error_message?: string; ip_address?: string; user_agent?: string; created_at: string; };
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLogsTotal, setAuditLogsTotal] = useState(0);
  const [auditLogsPage, setAuditLogsPage] = useState(1);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);

  const fetchAuditLogs = React.useCallback(async (page = 1) => {
    const adminToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!adminToken) return;
    setAuditLogsLoading(true);
    try {
      const r = await fetch(`${backendUrl}/api/sso/audit-logs?page=${page}&limit=10`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await r.json();
      setAuditLogs(data.logs || []);
      setAuditLogsTotal(data.total || 0);
      setAuditLogsPage(page);
    } catch {}
    setAuditLogsLoading(false);
  }, [backendUrl]);

  React.useEffect(() => {
    if (activeTab === 'settings') fetchAuditLogs(1);
  }, [activeTab, fetchAuditLogs]);

  // ── Active Directory state ──────────────────────────────────────────────────
  type GroupMapping = { ad_group_id: string; vela_role: string };
  const [adConfig, setAdConfig] = useState({
    enabled: false,
    tenant_id: '',
    client_id: '',
    client_secret: '',
    sync_interval_minutes: 60,
    group_mappings: [] as GroupMapping[],
    deactivate_removed_users: false,
  });
  const [adSyncStatus, setAdSyncStatus] = useState<{
    last_sync_at: string | null;
    last_sync_status: string | null;
    last_sync_created: number | null;
    last_sync_updated: number | null;
    last_sync_skipped: number | null;
    last_sync_errors: number | null;
  }>({ last_sync_at: null, last_sync_status: null, last_sync_created: null, last_sync_updated: null, last_sync_skipped: null, last_sync_errors: null });
  const [isSavingAd, setIsSavingAd] = useState(false);
  const [adSaveMsg, setAdSaveMsg] = useState<string | null>(null);
  const [isSyncingAd, setIsSyncingAd] = useState(false);
  const [adSyncMsg, setAdSyncMsg] = useState<string | null>(null);
  const [isTestingAd, setIsTestingAd] = useState(false);
  const [adTestMsg, setAdTestMsg] = useState<{ ok: boolean; text: string } | null>(null);

  type AdSyncLogEntry = { id: number; action: string; email?: string; display_name?: string; error_message?: string; sync_run_id?: string; created_at: string };
  const [adSyncLogs, setAdSyncLogs] = useState<AdSyncLogEntry[]>([]);
  const [adSyncLogsTotal, setAdSyncLogsTotal] = useState(0);
  const [adSyncLogsPage, setAdSyncLogsPage] = useState(1);
  const [adSyncLogsLoading, setAdSyncLogsLoading] = useState(false);

  const fetchAdConfig = React.useCallback(async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return;
    try {
      const r = await fetch(`${backendUrl}/api/ad/config`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setAdConfig((p) => ({ ...p, ...data, client_secret: '', group_mappings: data.group_mappings || [] }));
      setAdSyncStatus({
        last_sync_at: data.last_sync_at,
        last_sync_status: data.last_sync_status,
        last_sync_created: data.last_sync_created,
        last_sync_updated: data.last_sync_updated,
        last_sync_skipped: data.last_sync_skipped,
        last_sync_errors: data.last_sync_errors,
      });
    } catch {}
  }, [backendUrl]);

  const fetchAdSyncLogs = React.useCallback(async (page = 1) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return;
    setAdSyncLogsLoading(true);
    try {
      const r = await fetch(`${backendUrl}/api/ad/sync-logs?page=${page}&limit=15`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setAdSyncLogs(data.logs || []);
      setAdSyncLogsTotal(data.total || 0);
      setAdSyncLogsPage(page);
    } catch {}
    setAdSyncLogsLoading(false);
  }, [backendUrl]);

  React.useEffect(() => {
    if (activeTab === 'directory') {
      fetchAdConfig();
      fetchAdSyncLogs(1);
    }
  }, [activeTab, fetchAdConfig, fetchAdSyncLogs]);

  const handleSaveAd = async () => {
    setIsSavingAd(true);
    setAdSaveMsg(null);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const body: Record<string, any> = {
        tenant_id: adConfig.tenant_id,
        client_id: adConfig.client_id,
        enabled: adConfig.enabled,
        sync_interval_minutes: adConfig.sync_interval_minutes,
        group_mappings: adConfig.group_mappings,
        deactivate_removed_users: adConfig.deactivate_removed_users,
      };
      if (adConfig.client_secret) body.client_secret = adConfig.client_secret;
      const r = await fetch(`${backendUrl}/api/ad/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      setAdSaveMsg(r.ok ? 'AD configuration saved.' : 'Failed to save.');
    } catch { setAdSaveMsg('Failed to save.'); }
    setIsSavingAd(false);
    setTimeout(() => setAdSaveMsg(null), 3000);
  };

  const handleTestAdConnection = async () => {
    setIsTestingAd(true);
    setAdTestMsg(null);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await fetch(`${backendUrl}/api/ad/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tenant_id: adConfig.tenant_id, client_id: adConfig.client_id, client_secret: adConfig.client_secret }),
      });
      const data = await r.json();
      setAdTestMsg(data.success ? { ok: true, text: 'Connection successful! Credentials verified.' } : { ok: false, text: `Failed: ${JSON.stringify(data.error)}` });
    } catch (e: any) { setAdTestMsg({ ok: false, text: e.message }); }
    setIsTestingAd(false);
  };

  const handleTriggerAdSync = async () => {
    setIsSyncingAd(true);
    setAdSyncMsg(null);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await fetch(`${backendUrl}/api/ad/sync`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setAdSyncMsg(r.ok ? 'Sync started in background. Refresh logs in a moment.' : `Error: ${data.error}`);
      // Refresh config (last_sync stats) after a delay
      setTimeout(() => { fetchAdConfig(); fetchAdSyncLogs(1); }, 3000);
    } catch { setAdSyncMsg('Failed to trigger sync.'); }
    setIsSyncingAd(false);
  };

  React.useEffect(() => {
    const adminToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!adminToken) return;
    fetch(`${backendUrl}/api/sso/config`, {
      headers: { Authorization: `Bearer ${adminToken}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setSsoConfig((prev) => ({ ...prev, ...data, client_secret: '' }));
      })
      .catch(() => {});
  }, [backendUrl]);

  const handleSaveSso = async () => {
    setIsSavingSso(true);
    setSsoSaveMsg(null);
    try {
      const adminToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      const body: Record<string, any> = {
        issuer_url: ssoConfig.issuer_url,
        client_id: ssoConfig.client_id,
        enabled: ssoConfig.enabled,
        enforce_sso: ssoConfig.enforce_sso,
      };
      if (ssoConfig.client_secret) body.client_secret = ssoConfig.client_secret;
      const res = await fetch(`${backendUrl}/api/sso/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(body),
      });
      setSsoSaveMsg(res.ok ? 'SSO configuration saved.' : 'Failed to save.');
    } catch {
      setSsoSaveMsg('Failed to save.');
    } finally {
      setIsSavingSso(false);
      setTimeout(() => setSsoSaveMsg(null), 3000);
    }
  };

  // System Health state
  type ServiceStatus = "healthy" | "degraded" | "down";
  type HealthData = {
    cpuPercent: number;
    memoryPercent: number;
    diskPercent: number;
    uptimeHours: number;
    services: Array<{ name: string; status: ServiceStatus }>;
  };
  const [healthData, setHealthData] = useState<HealthData>({
    cpuPercent: 42,
    memoryPercent: 63,
    diskPercent: 58,
    uptimeHours: 124,
    services: [
      { name: "API Gateway", status: "healthy" },
      { name: "Auth Service", status: "healthy" },
      { name: "Media Server", status: "degraded" },
      { name: "DB Cluster", status: "healthy" },
    ],
  });
  const [lastHealthRefresh, setLastHealthRefresh] = useState<Date>(new Date());
  const refreshHealth = () => {
    const rand = (base: number) =>
      Math.min(99, Math.max(1, Math.round(base + (Math.random() * 10 - 5))));
    setHealthData((prev) => ({
      cpuPercent: rand(prev.cpuPercent),
      memoryPercent: rand(prev.memoryPercent),
      diskPercent: rand(prev.diskPercent),
      uptimeHours: prev.uptimeHours + 1,
      services: prev.services.map((svc) => {
        const r = Math.random();
        let status: ServiceStatus = svc.status;
        if (r < 0.05) status = "down";
        else if (r < 0.2) status = "degraded";
        else status = "healthy";
        return { ...svc, status };
      }),
    }));
    setLastHealthRefresh(new Date());
  };

  // Meeting Analytics state
  type TimeRange = "7d" | "30d" | "90d";
  const [analyticsRange, setAnalyticsRange] = useState<TimeRange>("7d");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const changeAnalyticsRange = async (range: TimeRange) => {
    setAnalyticsRange(range);
    setAnalyticsLoading(true);
    const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
    await fetchMeetingAnalytics(days);
    setAnalyticsLoading(false);
  };
  const trendLabels = React.useMemo(() => {
    const len = meetingAnalytics?.byPeriod?.length || 0;
    const now = new Date();
    if (analyticsRange === "7d") {
      const arr: string[] = [];
      for (let i = 0; i < len; i++) {
        const d = new Date();
        d.setDate(now.getDate() - (len - 1 - i));
        arr.push(d.toLocaleDateString(undefined, { weekday: "short" }));
      }
      return arr;
    }
    if (analyticsRange === "30d") {
      const arr: string[] = [];
      for (let i = 0; i < len; i++) {
        const d = new Date();
        d.setDate(now.getDate() - (len - 1 - i));
        arr.push(String(d.getDate()));
      }
      return arr;
    }
    const arr: string[] = [];
    for (let i = 0; i < len; i++) {
      const d = new Date();
      d.setDate(now.getDate() - (len - 1 - i) * 7);
      arr.push(d.toLocaleDateString(undefined, { month: "short" }));
    }
    return arr;
  }, [analyticsRange, meetingAnalytics?.byPeriod]);

  const trendMax = React.useMemo(
    () => Math.max(...(meetingAnalytics?.byPeriod || [1]), 1),
    [meetingAnalytics?.byPeriod]
  );
  const { trendAvg, trendTicks } = React.useMemo(() => {
    const values = meetingAnalytics?.byPeriod || [];
    console.log("values", values);

    const maxVal = Math.max(...values, 1);
    const avg = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    const step = Math.max(1, Math.ceil(maxVal / 4));
    const ticks = [0, step, step * 2, step * 3, step * 4];
    return { trendAvg: avg || 0, trendTicks: ticks };
  }, [meetingAnalytics?.byPeriod]);

  console.log("helllo", trendAvg, trendTicks);


  const handleClearNotification = (id: number) => {
    const notif = notifications.find((n) => n.id === id);
    if (notif) {
      setNotifications(notifications.filter((n) => n.id !== id));
      setClearedNotifications([notif, ...clearedNotifications]);
    }
  };

  const handleClearAll = () => {
    setClearedNotifications([...notifications, ...clearedNotifications]);
    setNotifications([]);
  };

  const handleRestoreNotification = (id: number) => {
    const notif = clearedNotifications.find((n) => n.id === id);
    if (notif) {
      setClearedNotifications(clearedNotifications.filter((n) => n.id !== id));
      setNotifications([notif, ...notifications]);
    }
  };


  const tabs = [
    { id: "overview", label: "Overview", icon: <Monitor size={20} /> },
    { id: "users", label: "User Management", icon: <Users size={20} /> },
    {
      id: "meetings",
      label: "Meeting Analytics",
      icon: <BarChart3 size={20} />,
    },
    { id: "settings", label: "System Settings", icon: <Settings size={20} /> },
    { id: "directory", label: "Directory", icon: <Database size={20} /> },
    // { id: "system", label: "System Health", icon: <Database size={20} /> },
  ];

  return (
    <div
      className={`min-h-screen ${darkMode ? "bg-[#0f1420]" : "bg-gray-50"
        } flex`}
    >
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      {/* <div
        className={`flex-1 ${
          collapsed ? "ml-20" : "ml-20 md:ml-56"
        } transition-all duration-200`}
      > */}
      <main className={`flex-1 px-6 py-8 md:px-12 md:py-10 transition-all duration-200 overflow-x-hidden ${collapsed ? "ml-20" : "ml-20 md:ml-52"
        }`}>
        {/* Top Bar - same as Dashboard */}
        <NavBar />

        {/* Section Title above tabs - matches Dashboard's Quick Actions */}
        <div className="mb-4 mt-4 -ml-4 md:-ml-6 lg:-ml-8">
          <div className="flex items-center gap-3 mb-4 pl-4 md:pl-6 lg:pl-8">
            <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
            <h3
              className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                }`}
            >
              Admin Portal
            </h3>
          </div>
        </div>

        {/* Tab Navigation */}
        <div
          className={`flex space-x-1 mb-8 rounded-lg p-1 shadow-sm border ${darkMode
              ? "bg-white/5 border-white/10"
              : "bg-white border-gray-200"
            }`}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === tab.id
                  ? "bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-sm"
                  : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/10"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">

              {stats.map((stat, index) => (
                <div
                  key={index}
                  className={`${darkMode
                      ? "bg-[#232336] border-white/10"
                      : "bg-white border-gray-200"
                    } rounded-lg shadow-sm border p-6`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-sm font-medium ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        {stat.label}
                      </p>
                      <p
                        className={`text-2xl font-bold mt-1 ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        {stat.value}
                      </p>
                    </div>
                    <div
                      className={`${stat.color} p-3 rounded-lg text-white`}
                    >
                      {stat.icon}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Meetings */}
              <div
                className={`${darkMode
                    ? "bg-[#232336] border-white/10"
                    : "bg-white border-gray-200"
                  } rounded-lg shadow-sm border`}
              >
                <div
                  className={`p-6 border-b ${darkMode ? "border-white/10" : "border-gray-200"
                    }`}
                >
                  <h3
                    className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    <CalendarCheck size={20} />
                    Recent Meetings
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {recentMeetingsStats?.length > 0 ? recentMeetingsStats.slice(0, 5).map((m, index) => (
                      <div
                        key={index}
                        className={`flex flex-col gap-2 [@media(min-width:500px)]:flex-row [@media(min-width:500px)]:justify-between
 sm:items-start sm:justify-between gap-3 p-4 rounded-lg border transition-colors ${darkMode
                            ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                            : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                          }`}
                      >



                        <div className="flex items-center gap-3">
                          {/* {m.hostAvatarUrl ? (
                              <img
                                src={m.hostAvatarUrl}
                                alt={m.host}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : ( */}
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-600 to-pink-600 text-white flex items-center justify-center text-xs font-semibold">
                            {m.title
                              .split(" ")
                              .map((t) => t[0])
                              .slice(0, 2)
                              .join("").toUpperCase()}
                          </div>
                          {/* )} */}
                          <div>
                            <p
                              className={`font-medium ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {m.title.charAt(0).toUpperCase() + m.title.slice(1)}
                            </p>
                            <p
                              className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                            >
                              Host: {m.created_by?.first_name} {m.created_by?.last_name}
                            </p>
                          </div>
                        </div>
                        <div
                          className="
    flex flex-col items-start sm:items-end gap-1 flex-shrink-0
    ml-[40px]
    [@media(min-width:500px)]:ml-0
  "
                        >




                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${darkMode
                                ? "bg-white/10 text-white/80"
                                : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {m.participantDetails.length} participants
                          </span>
                          <p
                            className={`text-xs mt-1 ${darkMode ? "text-white/50" : "text-gray-600"
                              }`}
                          >
                            Today • {m.start_time}
                          </p>
                        </div>
                      </div>
                    )) : <p className="text-center text-sm text-gray-500">No recent meetings</p>}
                  </div>
                </div>
              </div>

              {/* Top Users */}
              <div
                className={`${darkMode
                    ? "bg-[#232336] border-white/10"
                    : "bg-white border-gray-200"
                  } rounded-lg shadow-sm border`}
              >
                <div
                  className={`p-6 border-b ${darkMode ? "border-white/10" : "border-gray-200"
                    }`}
                >
                  <h3
                    className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    <Users size={20} />
                    Top Users
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {topUsersStats.length > 0 ? topUsersStats.map((u, index) => (
                      <div
                        key={index}
                        className={`flex flex-col gap-2 [@media(min-width:500px)]:flex-row [@media(min-width:500px)]:justify-between
 sm:items-start sm:justify-between gap-3 p-5 rounded-lg border transition-colors ${darkMode
                            ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                            : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                          }`}
                      >


                        <div className="flex items-center gap-3">
                          {u.avatarurl ? (
                            <img
                              src={backendUrl + u.avatarurl}
                              alt={u.first_name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-600 to-pink-600 text-white flex items-center justify-center text-xs font-semibold">
                              {u.first_name.split(" ")
                                .map((n) => n[0])
                                .slice(0, 2)
                                .join("").toUpperCase()

                                + " " + u.last_name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .slice(0, 2)
                                  .join("").toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p
                              className={`font-medium ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {u.first_name.charAt(0).toUpperCase() + u.first_name.slice(1) + " " + u.last_name.charAt(0).toUpperCase() + u.last_name.slice(1)}
                            </p>
                            <p
                              className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                            >
                              {u.job_title ? u.job_title : "No job title"} • {u.department ? u.department : "No department"}
                            </p>
                          </div>
                        </div>
                        <div
                          className="
    flex flex-col items-start sm:items-end gap-1 flex-shrink-0
    ml-[40px]
    [@media(min-width:500px)]:ml-0
  "
                        >




                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${darkMode
                                ? "bg-white/10 text-white/80"
                                : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {u.meeting_count} meetings
                          </span>
                        </div>
                      </div>
                    )) : <p className="text-center text-sm text-gray-500">No top users found</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}


        {activeTab === "users" && (
          <div
            className={`${darkMode ? "bg-[#232336] border-white/10" : "bg-white border-gray-200"
              } rounded-lg shadow-sm border p-6`}
          >
            <div className="w-full -mt-[0px]">
              <div className="w-full">
                <UserManagement darkMode={darkMode} />
              </div>
            </div>
          </div>
        )}




        {/* Meeting Analytics */}
        {activeTab === "meetings" && (
          <div
            className={`${darkMode
                ? "bg-[#232336] border-white/10"
                : "bg-white border-gray-200"
              } rounded-lg shadow-sm p-6`}
          >
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
              <div className="flex items-center gap-2">
                <BarChart3
                  size={20}
                  className={`${darkMode ? "text-white/80" : "text-gray-700"
                    }`}
                />
                <h3
                  className={`text-lg font-semibold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Meeting Analytics
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {(["7d", "30d", "90d"] as const).map((r) => (
                  <button
                    key={r}
                    onClick={() => changeAnalyticsRange(r)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border ${analyticsRange === r
                        ? "bg-gradient-to-r from-red-600 to-pink-600 text-white border-transparent"
                        : darkMode
                          ? "text-white/80 hover:bg-white/10 border-white/10"
                          : "text-gray-700 hover:bg-gray-100 border-gray-200"
                      }`}
                  >
                    {r.toUpperCase()}
                  </button>
                ))}
                <button
                  onClick={() => changeAnalyticsRange(analyticsRange)}
                  className={`p-2 rounded-md border ${analyticsLoading ? "opacity-60" : ""
                    } ${analyticsLoading
                      ? "cursor-not-allowed"
                      : darkMode
                        ? "hover:bg-white/10"
                        : "hover:bg-gray-100"
                    } ${darkMode ? "border-white/10" : "border-gray-200"}`}
                  disabled={analyticsLoading}
                  aria-label="Refresh analytics"
                >
                  <RefreshCw
                    className={`${analyticsLoading ? "animate-spin" : ""} ${darkMode ? "text-white/80" : "text-gray-700"
                      }`}
                    size={18}
                  />
                </button>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div
                className={`${darkMode ? "border-white/10" : "border-gray-200"
                  } rounded-lg border p-4`}
              >
                <p
                  className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  Total Meetings
                </p>
                <p
                  className={`mt-2 text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  {meetingAnalytics?.totalMeetings || 0}
                </p>
                <p
                  className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-500"
                    }`}
                >
                  Last {analyticsRange}
                </p>
              </div>
              <div
                className={`${darkMode ? "border-white/10" : "border-gray-200"
                  } rounded-lg border p-4`}
              >
                <p
                  className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  Average Duration
                </p>
                <p
                  className={`mt-2 text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  {meetingAnalytics?.avgDurationMin || 0}m
                </p>
                <p
                  className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-500"
                    }`}
                >
                  Per meeting
                </p>
              </div>
              <div
                className={`${darkMode ? "border-white/10" : "border-gray-200"
                  } rounded-lg border p-4`}
              >
                <p
                  className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  Total Participants
                </p>
                <p
                  className={`mt-2 text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  {meetingAnalytics?.totalParticipants || 0}
                </p>
                <p
                  className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-500"
                    }`}
                >
                  All meetings
                </p>
              </div>
              <div
                className={`${darkMode ? "border-white/10" : "border-gray-200"
                  } rounded-lg border p-4`}
              >
                <p
                  className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  Avg Participants
                </p>
                <p
                  className={`mt-2 text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  {meetingAnalytics?.avgParticipants || 0}
                </p>
                <p
                  className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-500"
                    }`}
                >
                  Per meeting
                </p>
              </div>
            </div>

            {/* Trend */}
            <div
              className={`mt-6 rounded-lg border ${darkMode ? "border-white/10" : ""
                }`}
            >
              <div
                className={`p-4 border-b ${darkMode ? "border-white/10" : ""
                  }`}
              >
                <h4
                  className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Activity Trend
                </h4>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-[32px_1fr] gap-2 items-end overflow-x-auto no-scrollbar">

                  {/* Y axis */}
                  <div
                    className={`h-40 flex flex-col justify-between text-[10px] ${darkMode ? "text-white/50" : "text-gray-500"
                      }`}
                  >
                    {trendTicks
                      .slice()
                      .reverse()
                      .map((t, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-1 -translate-y-1/2"
                        >
                          <span className="w-8 text-right">{t}</span>
                        </div>
                      ))}
                  </div>

                  {/* Chart area */}
                  <div className="relative">
                    {/* Gridlines */}
                    <div className="absolute inset-0 flex flex-col justify-between">
                      {trendTicks.map((_, i) => (
                        <div
                          key={i}
                          className={`h-px ${darkMode ? "bg-white/10" : "bg-gray-200/70"
                            }`}
                        />
                      ))}
                    </div>

                    {/* Bars */}
                    <div className="relative h-40 flex items-end gap-2">
                      {(meetingAnalytics?.byPeriod || []).map((v, i) => (
                        <div
                          key={i}
                          className="flex-1 flex flex-col items-center gap-1 group"
                        >
                          <div
                            className={`w-full rounded relative ${darkMode ? "bg-white/10" : "bg-gray-200"
                              }`}
                          >
                            <div
                              className={`bg-gradient-to-r from-pink-500 to-red-500 rounded`}
                              style={{
                                // height: `${Math.max(
                                //   6,
                                //   Math.round((v / trendMax) * 100)
                                // )}%`,
                                height:
                                  Math.round((v / trendMax) * 100)

                              }}
                            />
                            <div
                              className={`absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity rounded px-1 py-0.5 shadow-sm border ${darkMode
                                  ? "text-white bg-[#232336] border-white/10"
                                  : "text-gray-700 bg-white border-gray-200"
                                }`}
                            >
                              {v} meetings
                            </div>
                          </div>
                          <div
                            className={`text-[10px] ${darkMode ? "text-white/60" : "text-gray-500"
                              }`}
                          >
                            {trendLabels[i]}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Average line */}
                    <div
                      className={`absolute left-0 right-0 border-t-2 border-dashed ${darkMode ? "border-blue-500/70" : "border-blue-400"
                        }`}
                      style={{
                        bottom: `calc(${Math.max(
                          0,
                          Math.min(
                            100,
                            Math.round((trendAvg / trendMax) * 100)
                          )
                        )}% - 1px)`,
                      }}
                    >
                      <div
                        className={`absolute -top-4 right-0 text-[10px] px-1 rounded border ${darkMode
                            ? "text-blue-400 bg-[#232336] border-white/10"
                            : "text-blue-600 bg-white border"
                          } `}
                      >
                        avg {trendAvg}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div
                  className={`mt-3 flex items-center gap-4 text-[11px] ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-3 h-2 rounded bg-gradient-to-r from-pink-500 to-red-500" />
                    <span>Meetings</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className={`inline-block w-3 h-0.5 ${darkMode ? "bg-blue-400/80" : "bg-blue-400"
                        }`}
                    />
                    <span>Average</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Rooms */}
              <div
                className={`rounded-lg border ${darkMode ? "border-white/10" : ""
                  }`}
              >
                <div
                  className={`p-4 border-b ${darkMode ? "border-white/10" : ""
                    }`}
                >
                  <h4
                    className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Top Meeting Rooms
                  </h4>
                </div>
                <div className="p-4 space-y-3">
                  {topMeetingRooms.map((r, idx) => (
                    <div
                      key={idx}
                      className={`flex flex-col gap-2 [@media(min-width:500px)]:flex-row [@media(min-width:500px)]:justify-between
 sm:items-start sm:justify-between gap-3 p-4 rounded-lg border transition-colors ${darkMode
                          ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-600 to-pink-600 text-white flex items-center justify-center text-xs font-semibold">
                          {r.name
                            .split(" ")
                            .map((n) => n[0])
                            .slice(0, 2)
                            .join("")}
                        </div>
                        <div>
                          <p
                            className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            {r.name}
                          </p>
                          <p
                            className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          >
                            {r.participants} participants
                          </p>
                        </div>
                      </div>
                      <div
                        className="
    flex flex-col items-start sm:items-end gap-1 flex-shrink-0
    ml-[40px]
    [@media(min-width:500px)]:ml-0
  "
                      >



                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${darkMode
                              ? "bg-white/10 text-white/80"
                              : "bg-gray-100 text-gray-800"
                            }`}
                        >
                          {r.avgDurationMin}m avg
                        </span>
                        <p
                          className={`text-xs mt-1 ${darkMode ? "text-white/60" : "text-gray-600"
                            }`}
                        >
                          {r.meetings} meetings
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

             {/* Meeting Metrics Section */}
<div className={`rounded-lg border ${darkMode ? "border-white/10" : "border-gray-200"}`}>
  <div className={`p-4 border-b ${darkMode ? "border-white/10" : "border-gray-200"}`}>
    <h4 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>
      Meeting Metrics
    </h4>
  </div>
  <div className="p-4 space-y-3">
    {[
      { label: "Completion Rate", value: meetingAnalytics?.completionRate || 0 },
      { label: "Rescheduled", value: meetingAnalytics?.rescheduleRate || 0 },
      { label: "Cancellation", value: meetingAnalytics?.cancelRate || 0 },
    ].map((metric, idx) => (
      <div
        key={idx}
        className={`flex flex-col justify-center gap-3 p-4 rounded-lg border transition-all duration-200 ${
          darkMode
            ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
            : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
        }`}
        style={{ minHeight: "82px" }} // Meeting room waale card ki height se match karne ke liye
      >
        <div className="flex items-center justify-between">
          <span className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-600"}`}>
            {metric.label}
          </span>
          <span className={`text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
            {metric.value}%
          </span>
        </div>
        
        <div className={`h-2 w-full rounded-full ${darkMode ? "bg-white/10" : "bg-gray-200"}`}>
          <div
            className="h-2 rounded-full bg-gradient-to-r from-red-600 to-pink-600 transition-all duration-500"
            style={{ width: `${metric.value}%` }}
          />
        </div>
      </div>
    ))}
  </div>
</div>
            </div>
          </div>
        )}

        {/* System Settings */}
        {activeTab === "settings" && (
          <div
            className={`${darkMode
                ? "bg-[#232336] border-white/10"
                : "bg-white border-gray-200"
              } rounded-lg shadow-sm border p-6`}
          >
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <Settings
                  size={20}
                  className={`${darkMode ? "text-white/80" : "text-gray-700"
                    }`}
                />
                <h3
                  className={`text-lg font-semibold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  System Settings
                </h3>
              </div>
              <p
                className={`text-sm mt-1 ${darkMode ? "text-white/60" : "text-gray-600"
                  }`}
              >
                Configure platform preferences, security, notifications, and
                maintenance.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* General */}
              <div
                className={`${darkMode
                    ? "rounded-lg border border-white/10 p-4"
                    : "rounded-lg border border-gray-200 p-4"
                  }`}
              >
                <h4
                  className={`font-semibold mb-4 ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  General
                </h4>
                <div className="space-y-4">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      {/* 👈 Change: items-center to items-start, added gap-4 for spacing */}
                      <div className="min-w-0 flex-grow">
                        {/* 👈 Change: Added min-w-0 and flex-grow to make the text responsive */}
                        <p
                          className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          Password expiry
                        </p>
                        <p
                          className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                            }`}
                        >
                          Enforce password rotation after set days.
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        {/*  New wrapper div with flex-shrink-0 to prevent toggle from shrinking */}
                        <button
                          type="button"
                          onClick={() =>
                            handleSystemSettingToggle("enablePasswordExpiry")
                          }
                          className={`${systemSettings.enablePasswordExpiry
                              ? "bg-gradient-to-r from-red-600 to-pink-600"
                              : "bg-gray-200"
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                          aria-pressed={systemSettings.enablePasswordExpiry}
                        >
                          <span
                            className={`${systemSettings.enablePasswordExpiry
                                ? "translate-x-6"
                                : "translate-x-1"
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </button>
                      </div>
                    </div>
                    {systemSettings.enablePasswordExpiry && (
                      <div>
                        <label
                          className={`block text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          Password expiry (days)
                        </label>
                        <input
                          type="number"
                          min={15}
                          className={`mt-1 w-full rounded-md text-sm focus:border-pink-500 focus:ring-pink-500 ${darkMode
                              ? "bg-[#0f1420] border-white/10 text-white placeholder-white/40"
                              : "border-gray-300"
                            }`}
                          value={systemSettings.passwordExpiryDays}
                          onChange={(e) =>
                            handleSystemSettingChange(
                              "passwordExpiryDays",
                              Number(e.target.value)
                            )
                          }
                        />
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p
                          className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          Session timeout
                        </p>
                        <p
                          className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                            }`}
                        >
                          Auto sign out after inactivity.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleSystemSettingToggle("enableSessionTimeout")
                        }
                        className={`${systemSettings.enableSessionTimeout
                            ? "bg-gradient-to-r from-red-600 to-pink-600"
                            : "bg-gray-200"
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                        aria-pressed={systemSettings.enableSessionTimeout}
                      >
                        <span
                          className={`${systemSettings.enableSessionTimeout
                              ? "translate-x-6"
                              : "translate-x-1"
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                    </div>
                    {systemSettings.enableSessionTimeout && (
                      <div>
                        <label
                          className={`block text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          Session timeout (mins)
                        </label>
                        <input
                          type="number"
                          min={5}
                          className={`mt-1 w-full rounded-md text-sm focus:border-pink-500 focus:ring-pink-500 ${darkMode
                              ? "bg-[#0f1420] border-white/10 text-white placeholder-white/40"
                              : "border-gray-300"
                            }`}
                          value={systemSettings.sessionTimeoutMinutes}
                          onChange={(e) =>
                            handleSystemSettingChange(
                              "sessionTimeoutMinutes",
                              Number(e.target.value)
                            )
                          }
                        />
                      </div>
                    )}
                    {systemSettings.enableMeetingRoomTimeout && (
                      <div>
                        <label
                          className={`block text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          Meeting room timeout (mins)
                        </label>
                        <input
                          type="number"
                          min={5}
                          className={`mt-1 w-full rounded-md text-sm focus:border-pink-500 focus:ring-pink-500 ${darkMode
                              ? "bg-[#0f1420] border-white/10 text-white placeholder-white/40"
                              : "border-gray-300"
                            }`}
                          value={systemSettings.meetingRoomTimeoutMinutes}
                          onChange={(e) =>
                            handleSystemSettingChange(
                              "meetingRoomTimeoutMinutes",
                              Number(e.target.value)
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Security */}
              <div
                className={`${darkMode
                    ? "rounded-lg border border-white/10 p-4"
                    : "rounded-lg border border-gray-200 p-4"
                  }`}
              >
                <h4
                  className={`font-semibold mb-4 ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Security
                </h4>
                <div className="space-y-4">
                  {/* ENFORCE 2FA TOGGLE FIX */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-grow">
                      <p
                        className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Enforce 2FA
                      </p>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Require two-factor authentication for all users.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          handleSystemSettingToggle("enforceTwoFactor")
                        }
                        className={`${systemSettings.enforceTwoFactor
                            ? "bg-gradient-to-r from-red-600 to-pink-600"
                            : "bg-gray-200"
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                        aria-pressed={systemSettings.enforceTwoFactor}
                      >
                        <span
                          className={`${systemSettings.enforceTwoFactor
                              ? "translate-x-6"
                              : "translate-x-1"
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* ALLOW GUEST ACCESS TOGGLE FIX */}
                  {/* <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-grow">
                      <p
                        className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Allow guest access
                      </p>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Permit guests to join with limited permissions.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          handleSystemSettingToggle("allowGuestAccess")
                        }
                        className={`${systemSettings.allowGuestAccess
                            ? "bg-gradient-to-r from-red-600 to-pink-600"
                            : "bg-gray-200"
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                        aria-pressed={systemSettings.allowGuestAccess}
                      >
                        <span
                          className={`${systemSettings.allowGuestAccess
                              ? "translate-x-6"
                              : "translate-x-1"
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Notifications */}
              <div
                className={`${darkMode
                    ? "rounded-lg border border-white/10 p-4"
                    : "rounded-lg border border-gray-200 p-4"
                  }`}
              >
                <h4
                  className={`font-semibold mb-4 ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Notifications
                </h4>
                <div className="space-y-4">
                  {/* EMAIL NOTIFICATIONS TOGGLE FIX */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-grow">
                      <p
                        className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Email notifications
                      </p>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Send email alerts for important events.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          handleSystemSettingToggle("enableEmailNotifications")
                        }
                        className={`${systemSettings.enableEmailNotifications
                            ? "bg-gradient-to-r from-red-600 to-pink-600"
                            : "bg-gray-200"
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                        aria-pressed={systemSettings.enableEmailNotifications}
                      >
                        <span
                          className={`${systemSettings.enableEmailNotifications
                              ? "translate-x-6"
                              : "translate-x-1"
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* SYSTEM ALERTS TOGGLE FIX */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-grow">
                      <p
                        className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        System alerts
                      </p>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Display critical health alerts in the dashboard.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          handleSystemSettingToggle("enableSystemAlerts")
                        }
                        className={`${systemSettings.enableSystemAlerts
                            ? "bg-gradient-to-r from-red-600 to-pink-600"
                            : "bg-gray-200"
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                        aria-pressed={systemSettings.enableSystemAlerts}
                      >
                        <span
                          className={`${systemSettings.enableSystemAlerts
                              ? "translate-x-6"
                              : "translate-x-1"
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Platform Announcement */}
              <div
                className={`${darkMode
                    ? "rounded-lg border border-white/10 p-4"
                    : "rounded-lg border border-gray-200 p-4"
                  }`}
              >
                <h4
                  className={`font-semibold mb-4 ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Platform Announcement
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`block text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Title (optional)
                    </label>
                    <input
                      type="text"
                      className={`mt-1 w-full rounded-md text-sm focus:border-pink-500 focus:ring-pink-500 ${darkMode
                          ? "bg-[#0f1420] border-white/10 text-white placeholder-white/40"
                          : "border-gray-300"
                        }`}
                      placeholder="e.g., Scheduled maintenance"
                      value={announcementTitle}
                      onChange={(e) => setAnnouncementTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Severity
                    </label>
                    <select
                      className={`mt-1 w-full rounded-md text-sm focus:border-pink-500 focus:ring-pink-500 ${darkMode
                          ? "bg-[#0f1420] border-white/10 text-white"
                          : "border-gray-300"
                        }`}
                      value={announcementSeverity}
                      onChange={(e) =>
                        setAnnouncementSeverity(e.target.value as any)
                      }
                    >
                      <option value="info">Info</option>
                      <option value="warning">Warning</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Audience
                    </label>
                    <select
                      className={`mt-1 w-full rounded-md text-sm focus:border-pink-500 focus:ring-pink-500 ${darkMode
                          ? "bg-[#0f1420] border-white/10 text-white"
                          : "border-gray-300"
                        }`}
                      value={announcementAudience}
                      onChange={(e) =>
                        setAnnouncementAudience(e.target.value as any)
                      }
                    >
                      <option value="all">All users</option>
                      <option value="admins">Admins only</option>
                      <option value="standard">Standard users</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Expiry (optional)
                    </label>
                    <input
                      type="datetime-local"
                      className={`mt-1 w-full rounded-md text-sm focus:border-pink-500 focus:ring-pink-500 ${darkMode
                          ? "bg-[#0f1420] border-white/10 text-white"
                          : "border-gray-300"
                        }`}
                      value={announcementExpiry}
                      onChange={(e) => setAnnouncementExpiry(e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label
                      className={`block text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Message
                    </label>
                    <textarea
                      className={`mt-1 w-full rounded-md text-sm focus:border-pink-500 focus:ring-pink-500 ${darkMode
                          ? "bg-[#0f1420] border-white/10 text-white placeholder-white/40"
                          : "border-gray-300"
                        }`}
                      rows={4}
                      placeholder="Write your announcement..."
                      value={announcementMessage}
                      onChange={(e) => setAnnouncementMessage(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4">
                  <button
                    onClick={sendPlatformAnnouncement}
                    disabled={
                      isSendingAnnouncement || !announcementMessage.trim()
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 px-4 py-2 text-white font-medium shadow disabled:opacity-60"
                  >
                    {isSendingAnnouncement && (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    )}
                    Send Announcement
                  </button>
                </div>
              </div>
            </div>

            {/* SSO Configuration */}
            <div className={`mt-6 ${darkMode ? "rounded-lg border border-white/10 p-4" : "rounded-lg border border-gray-200 p-4"}`}>
              <h4 className={`font-semibold mb-1 ${darkMode ? "text-white" : "text-gray-900"}`}>
                Single Sign-On (SSO)
              </h4>
              <p className={`text-xs mb-4 ${darkMode ? "text-white/60" : "text-gray-500"}`}>
                Configure OIDC/SAML enterprise login. Works with Okta, Azure AD, Google Workspace, and any OpenID Connect provider.
              </p>

              <div className="space-y-4">
                {/* Enabled toggle */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>Enable SSO</p>
                    <p className={`text-xs ${darkMode ? "text-white/60" : "text-gray-500"}`}>Show "Sign in with SSO" button on the login page.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSsoConfig((p) => ({ ...p, enabled: !p.enabled }))}
                    className={`${ssoConfig.enabled ? "bg-gradient-to-r from-red-600 to-pink-600" : "bg-gray-200"} relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0`}
                  >
                    <span className={`${ssoConfig.enabled ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                  </button>
                </div>

                {/* Enforce SSO toggle */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>Enforce SSO</p>
                    <p className={`text-xs ${darkMode ? "text-white/60" : "text-gray-500"}`}>When enabled, users cannot log in with email/password.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSsoConfig((p) => ({ ...p, enforce_sso: !p.enforce_sso }))}
                    className={`${ssoConfig.enforce_sso ? "bg-gradient-to-r from-red-600 to-pink-600" : "bg-gray-200"} relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0`}
                  >
                    <span className={`${ssoConfig.enforce_sso ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                  </button>
                </div>

                {/* Issuer URL */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-white/80" : "text-gray-700"}`}>
                    Issuer URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://accounts.google.com"
                    value={ssoConfig.issuer_url}
                    onChange={(e) => setSsoConfig((p) => ({ ...p, issuer_url: e.target.value }))}
                    className={`w-full rounded-md text-sm px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? "bg-[#0f1420] border-white/10 text-white placeholder-white/40" : "border-gray-300 text-gray-900"}`}
                  />
                  <p className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-400"}`}>Discovery document fetched from {"<issuer>/.well-known/openid-configuration"}</p>
                </div>

                {/* Client ID */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-white/80" : "text-gray-700"}`}>
                    Client ID
                  </label>
                  <input
                    type="text"
                    placeholder="your-client-id"
                    value={ssoConfig.client_id}
                    onChange={(e) => setSsoConfig((p) => ({ ...p, client_id: e.target.value }))}
                    className={`w-full rounded-md text-sm px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? "bg-[#0f1420] border-white/10 text-white placeholder-white/40" : "border-gray-300 text-gray-900"}`}
                  />
                </div>

                {/* Client Secret */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-white/80" : "text-gray-700"}`}>
                    Client Secret
                  </label>
                  <input
                    type="password"
                    placeholder="Leave blank to keep existing secret"
                    value={ssoConfig.client_secret}
                    onChange={(e) => setSsoConfig((p) => ({ ...p, client_secret: e.target.value }))}
                    className={`w-full rounded-md text-sm px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? "bg-[#0f1420] border-white/10 text-white placeholder-white/40" : "border-gray-300 text-gray-900"}`}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <button
                    onClick={handleSaveSso}
                    disabled={isSavingSso}
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 px-4 py-2 text-white text-sm font-medium shadow disabled:opacity-60"
                  >
                    {isSavingSso && <RefreshCw className="h-4 w-4 animate-spin" />}
                    Save SSO Config
                  </button>
                  {ssoSaveMsg && (
                    <span className={`text-sm ${ssoSaveMsg.includes('Failed') ? 'text-red-500' : 'text-green-500'}`}>
                      {ssoSaveMsg}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* SSO Audit Logs */}
            <div className={`mt-6 ${darkMode ? "rounded-lg border border-white/10 p-4" : "rounded-lg border border-gray-200 p-4"}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>SSO Audit Log</h4>
                  <p className={`text-xs ${darkMode ? "text-white/60" : "text-gray-500"}`}>Login attempts via SSO — who, when, success or failure.</p>
                </div>
                <button
                  onClick={() => fetchAuditLogs(auditLogsPage)}
                  disabled={auditLogsLoading}
                  className={`p-2 rounded-md border ${darkMode ? "border-white/10 hover:bg-white/10" : "border-gray-200 hover:bg-gray-100"}`}
                  aria-label="Refresh audit logs"
                >
                  <RefreshCw className={`h-4 w-4 ${auditLogsLoading ? "animate-spin" : ""} ${darkMode ? "text-white/60" : "text-gray-500"}`} />
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`text-left text-xs font-medium border-b ${darkMode ? "text-white/50 border-white/10" : "text-gray-500 border-gray-200"}`}>
                      <th className="pb-2 pr-4">Email</th>
                      <th className="pb-2 pr-4">Status</th>
                      <th className="pb-2 pr-4">Provider</th>
                      <th className="pb-2 pr-4">IP</th>
                      <th className="pb-2 pr-4">Error</th>
                      <th className="pb-2">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogsLoading && (
                      <tr><td colSpan={6} className={`py-4 text-center text-xs ${darkMode ? "text-white/40" : "text-gray-400"}`}>Loading...</td></tr>
                    )}
                    {!auditLogsLoading && auditLogs.length === 0 && (
                      <tr><td colSpan={6} className={`py-4 text-center text-xs ${darkMode ? "text-white/40" : "text-gray-400"}`}>No SSO login attempts yet.</td></tr>
                    )}
                    {auditLogs.map((log) => (
                      <tr key={log.id} className={`border-b ${darkMode ? "border-white/5" : "border-gray-100"}`}>
                        <td className={`py-2 pr-4 ${darkMode ? "text-white/80" : "text-gray-800"}`}>{log.email || '—'}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${log.status === 'success' ? (darkMode ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700") : (darkMode ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-700")}`}>
                            {log.status}
                          </span>
                        </td>
                        <td className={`py-2 pr-4 ${darkMode ? "text-white/60" : "text-gray-600"}`}>{log.provider || '—'}</td>
                        <td className={`py-2 pr-4 text-xs ${darkMode ? "text-white/50" : "text-gray-500"}`}>{log.ip_address || '—'}</td>
                        <td className={`py-2 pr-4 text-xs max-w-[180px] truncate ${darkMode ? "text-white/50" : "text-gray-500"}`} title={log.error_message}>{log.error_message || '—'}</td>
                        <td className={`py-2 text-xs ${darkMode ? "text-white/50" : "text-gray-500"}`}>{new Date(log.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {auditLogsTotal > 10 && (
                <div className="flex items-center justify-between mt-3">
                  <span className={`text-xs ${darkMode ? "text-white/40" : "text-gray-400"}`}>{auditLogsTotal} total entries</span>
                  <div className="flex gap-2">
                    <button
                      disabled={auditLogsPage <= 1}
                      onClick={() => fetchAuditLogs(auditLogsPage - 1)}
                      className={`px-3 py-1 text-xs rounded border disabled:opacity-40 ${darkMode ? "border-white/10 text-white/60 hover:bg-white/10" : "border-gray-200 text-gray-600 hover:bg-gray-100"}`}
                    >Prev</button>
                    <button
                      disabled={auditLogsPage * 10 >= auditLogsTotal}
                      onClick={() => fetchAuditLogs(auditLogsPage + 1)}
                      className={`px-3 py-1 text-xs rounded border disabled:opacity-40 ${darkMode ? "border-white/10 text-white/60 hover:bg-white/10" : "border-gray-200 text-gray-600 hover:bg-gray-100"}`}
                    >Next</button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end mt-6">
              <button
                onClick={handleSaveSystemSettings}
                disabled={isSavingSettings}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 px-4 py-2 text-white font-medium shadow disabled:opacity-60"
              >
                {isSavingSettings && (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        )}

        {/* ── Directory (Active Directory / Azure AD sync) ───────────── */}
        {activeTab === "directory" && (
          <div className={`${darkMode ? "bg-[#232336] border-white/10" : "bg-white border-gray-200"} rounded-lg shadow-sm border p-6 space-y-6`}>

            {/* Header */}
            <div className="flex items-center gap-2">
              <Database size={20} className={darkMode ? "text-white/80" : "text-gray-700"} />
              <div>
                <h3 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>Active Directory / Azure AD</h3>
                <p className={`text-xs mt-0.5 ${darkMode ? "text-white/50" : "text-gray-500"}`}>
                  Automatically sync employees from your Azure AD tenant using Microsoft Graph API.
                </p>
              </div>
            </div>

            {/* ── Sync Status Banner ─────────────────────────────────────── */}
            {adSyncStatus.last_sync_at && (
              <div className={`rounded-lg border p-4 ${
                adSyncStatus.last_sync_status === 'success' ? (darkMode ? "border-green-500/30 bg-green-500/10" : "border-green-200 bg-green-50") :
                adSyncStatus.last_sync_status === 'partial' ? (darkMode ? "border-yellow-500/30 bg-yellow-500/10" : "border-yellow-200 bg-yellow-50") :
                (darkMode ? "border-red-500/30 bg-red-500/10" : "border-red-200 bg-red-50")
              }`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                      Last sync: <span className={`capitalize ${adSyncStatus.last_sync_status === 'success' ? "text-green-500" : adSyncStatus.last_sync_status === 'partial' ? "text-yellow-500" : "text-red-500"}`}>{adSyncStatus.last_sync_status}</span>
                    </p>
                    <p className={`text-xs mt-0.5 ${darkMode ? "text-white/50" : "text-gray-500"}`}>{new Date(adSyncStatus.last_sync_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-4 text-xs">
                    <span className="text-green-500">+{adSyncStatus.last_sync_created ?? 0} created</span>
                    <span className={darkMode ? "text-white/60" : "text-gray-600"}>~{adSyncStatus.last_sync_updated ?? 0} updated</span>
                    <span className={darkMode ? "text-white/40" : "text-gray-400"}>{adSyncStatus.last_sync_skipped ?? 0} skipped</span>
                    {(adSyncStatus.last_sync_errors ?? 0) > 0 && <span className="text-red-500">{adSyncStatus.last_sync_errors} errors</span>}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ── Connection Config ──────────────────────────────────────── */}
              <div className={`rounded-lg border p-4 space-y-4 ${darkMode ? "border-white/10" : "border-gray-200"}`}>
                <h4 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>Connection</h4>

                {/* Enable toggle */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>Enable AD Sync</p>
                    <p className={`text-xs ${darkMode ? "text-white/50" : "text-gray-500"}`}>Turn on automatic user provisioning from Azure AD.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAdConfig((p) => ({ ...p, enabled: !p.enabled }))}
                    className={`${adConfig.enabled ? "bg-gradient-to-r from-red-600 to-pink-600" : "bg-gray-200"} relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0`}
                  >
                    <span className={`${adConfig.enabled ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                  </button>
                </div>

                {/* Tenant ID */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-white/80" : "text-gray-700"}`}>Tenant ID</label>
                  <input
                    type="text"
                    placeholder="your-tenant.onmicrosoft.com or GUID"
                    value={adConfig.tenant_id}
                    onChange={(e) => setAdConfig((p) => ({ ...p, tenant_id: e.target.value }))}
                    className={`w-full rounded-md text-sm px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? "bg-[#0f1420] border-white/10 text-white placeholder-white/30" : "border-gray-300 text-gray-900"}`}
                  />
                  <p className={`text-xs mt-1 ${darkMode ? "text-white/30" : "text-gray-400"}`}>Found in Azure Portal → Azure Active Directory → Overview</p>
                </div>

                {/* Client ID */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-white/80" : "text-gray-700"}`}>Client ID (Application ID)</label>
                  <input
                    type="text"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={adConfig.client_id}
                    onChange={(e) => setAdConfig((p) => ({ ...p, client_id: e.target.value }))}
                    className={`w-full rounded-md text-sm px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? "bg-[#0f1420] border-white/10 text-white placeholder-white/30" : "border-gray-300 text-gray-900"}`}
                  />
                </div>

                {/* Client Secret */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-white/80" : "text-gray-700"}`}>Client Secret</label>
                  <input
                    type="password"
                    placeholder="Leave blank to keep existing secret"
                    value={adConfig.client_secret}
                    onChange={(e) => setAdConfig((p) => ({ ...p, client_secret: e.target.value }))}
                    className={`w-full rounded-md text-sm px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? "bg-[#0f1420] border-white/10 text-white placeholder-white/30" : "border-gray-300 text-gray-900"}`}
                  />
                  <p className={`text-xs mt-1 ${darkMode ? "text-white/30" : "text-gray-400"}`}>App needs Application permissions: User.Read.All, Group.Read.All (admin consent required)</p>
                </div>

                {/* Test Connection */}
                <div>
                  <button
                    onClick={handleTestAdConnection}
                    disabled={isTestingAd || !adConfig.tenant_id || !adConfig.client_id || !adConfig.client_secret}
                    className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-40 ${darkMode ? "border-white/20 text-white/80 hover:bg-white/10" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                  >
                    {isTestingAd && <RefreshCw className="h-4 w-4 animate-spin" />}
                    Test Connection
                  </button>
                  {adTestMsg && (
                    <p className={`text-xs mt-2 ${adTestMsg.ok ? "text-green-500" : "text-red-500"}`}>{adTestMsg.text}</p>
                  )}
                </div>
              </div>

              {/* ── Sync Settings ──────────────────────────────────────────── */}
              <div className={`rounded-lg border p-4 space-y-4 ${darkMode ? "border-white/10" : "border-gray-200"}`}>
                <h4 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>Sync Settings</h4>

                {/* Sync interval */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-white/80" : "text-gray-700"}`}>Auto-sync every (minutes)</label>
                  <input
                    type="number"
                    min={0}
                    placeholder="0 = manual only"
                    value={adConfig.sync_interval_minutes}
                    onChange={(e) => setAdConfig((p) => ({ ...p, sync_interval_minutes: Number(e.target.value) }))}
                    className={`w-full rounded-md text-sm px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? "bg-[#0f1420] border-white/10 text-white placeholder-white/30" : "border-gray-300 text-gray-900"}`}
                  />
                  <p className={`text-xs mt-1 ${darkMode ? "text-white/30" : "text-gray-400"}`}>Set 0 to disable auto-sync. Typical value: 60 (every hour).</p>
                </div>

                {/* Deactivate removed users */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>Deactivate removed users</p>
                    <p className={`text-xs ${darkMode ? "text-white/50" : "text-gray-500"}`}>When a user is removed from AD, block their VELA login.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAdConfig((p) => ({ ...p, deactivate_removed_users: !p.deactivate_removed_users }))}
                    className={`${adConfig.deactivate_removed_users ? "bg-gradient-to-r from-red-600 to-pink-600" : "bg-gray-200"} relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0`}
                  >
                    <span className={`${adConfig.deactivate_removed_users ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                  </button>
                </div>

                {/* Group → Role mappings */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`text-sm font-medium ${darkMode ? "text-white/80" : "text-gray-700"}`}>Group → Role Mappings</label>
                    <button
                      onClick={() => setAdConfig((p) => ({ ...p, group_mappings: [...p.group_mappings, { ad_group_id: '', vela_role: 'admin' }] }))}
                      className={`text-xs px-2 py-1 rounded border ${darkMode ? "border-white/20 text-white/70 hover:bg-white/10" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}
                    >+ Add Mapping</button>
                  </div>
                  <p className={`text-xs mb-2 ${darkMode ? "text-white/30" : "text-gray-400"}`}>Members of an AD group get the VELA role. Group ID from Azure AD → Groups → click group → Object ID.</p>
                  {adConfig.group_mappings.length === 0 && (
                    <p className={`text-xs ${darkMode ? "text-white/30" : "text-gray-400"}`}>No mappings — all synced users get standard user role.</p>
                  )}
                  {adConfig.group_mappings.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="AD Group Object ID"
                        value={m.ad_group_id}
                        onChange={(e) => setAdConfig((p) => {
                          const gm = [...p.group_mappings];
                          gm[idx] = { ...gm[idx], ad_group_id: e.target.value };
                          return { ...p, group_mappings: gm };
                        })}
                        className={`flex-1 rounded-md text-xs px-2 py-1.5 border focus:outline-none focus:ring-1 focus:ring-pink-500 ${darkMode ? "bg-[#0f1420] border-white/10 text-white placeholder-white/30" : "border-gray-300 text-gray-900"}`}
                      />
                      <select
                        value={m.vela_role}
                        onChange={(e) => setAdConfig((p) => {
                          const gm = [...p.group_mappings];
                          gm[idx] = { ...gm[idx], vela_role: e.target.value };
                          return { ...p, group_mappings: gm };
                        })}
                        className={`rounded-md text-xs px-2 py-1.5 border ${darkMode ? "bg-[#0f1420] border-white/10 text-white" : "border-gray-300 text-gray-900"}`}
                      >
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </select>
                      <button
                        onClick={() => setAdConfig((p) => ({ ...p, group_mappings: p.group_mappings.filter((_, i) => i !== idx) }))}
                        className="text-red-400 hover:text-red-600 text-xs px-1"
                      >✕</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Save + Manual Sync buttons */}
            <div className="flex items-center gap-4 flex-wrap">
              <button
                onClick={handleSaveAd}
                disabled={isSavingAd}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 px-4 py-2 text-white text-sm font-medium shadow disabled:opacity-60"
              >
                {isSavingAd && <RefreshCw className="h-4 w-4 animate-spin" />}
                Save AD Config
              </button>
              <button
                onClick={handleTriggerAdSync}
                disabled={isSyncingAd || !adConfig.enabled}
                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-40 ${darkMode ? "border-white/20 text-white/80 hover:bg-white/10" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
              >
                {isSyncingAd && <RefreshCw className="h-4 w-4 animate-spin" />}
                Sync Now
              </button>
              {adSaveMsg && <span className={`text-sm ${adSaveMsg.includes('Failed') ? "text-red-500" : "text-green-500"}`}>{adSaveMsg}</span>}
              {adSyncMsg && <span className={`text-sm ${adSyncMsg.startsWith('Error') || adSyncMsg.startsWith('Failed') ? "text-red-500" : "text-blue-500"}`}>{adSyncMsg}</span>}
            </div>

            {/* ── Sync Log Table ────────────────────────────────────────────── */}
            <div className={`rounded-lg border p-4 ${darkMode ? "border-white/10" : "border-gray-200"}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>Sync Log</h4>
                  <p className={`text-xs ${darkMode ? "text-white/50" : "text-gray-500"}`}>Every user action during sync — created, updated, deactivated, errors.</p>
                </div>
                <button
                  onClick={() => fetchAdSyncLogs(adSyncLogsPage)}
                  disabled={adSyncLogsLoading}
                  className={`p-2 rounded-md border ${darkMode ? "border-white/10 hover:bg-white/10" : "border-gray-200 hover:bg-gray-100"}`}
                >
                  <RefreshCw className={`h-4 w-4 ${adSyncLogsLoading ? "animate-spin" : ""} ${darkMode ? "text-white/60" : "text-gray-500"}`} />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`text-left text-xs font-medium border-b ${darkMode ? "text-white/50 border-white/10" : "text-gray-500 border-gray-200"}`}>
                      <th className="pb-2 pr-4">Action</th>
                      <th className="pb-2 pr-4">Email</th>
                      <th className="pb-2 pr-4">Display Name</th>
                      <th className="pb-2 pr-4">Error</th>
                      <th className="pb-2">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adSyncLogsLoading && (
                      <tr><td colSpan={5} className={`py-4 text-center text-xs ${darkMode ? "text-white/40" : "text-gray-400"}`}>Loading...</td></tr>
                    )}
                    {!adSyncLogsLoading && adSyncLogs.length === 0 && (
                      <tr><td colSpan={5} className={`py-4 text-center text-xs ${darkMode ? "text-white/40" : "text-gray-400"}`}>No sync activity yet. Click "Sync Now" to start.</td></tr>
                    )}
                    {adSyncLogs.map((log) => (
                      <tr key={log.id} className={`border-b ${darkMode ? "border-white/5" : "border-gray-100"}`}>
                        <td className="py-2 pr-4">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            log.action === 'created' ? (darkMode ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700") :
                            log.action === 'updated' ? (darkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700") :
                            log.action === 'deactivated' ? (darkMode ? "bg-orange-500/20 text-orange-400" : "bg-orange-100 text-orange-700") :
                            log.action === 'error' ? (darkMode ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-700") :
                            (darkMode ? "bg-white/10 text-white/60" : "bg-gray-100 text-gray-600")
                          }`}>{log.action}</span>
                        </td>
                        <td className={`py-2 pr-4 ${darkMode ? "text-white/80" : "text-gray-800"}`}>{log.email || '—'}</td>
                        <td className={`py-2 pr-4 ${darkMode ? "text-white/60" : "text-gray-600"}`}>{log.display_name || '—'}</td>
                        <td className={`py-2 pr-4 text-xs max-w-[200px] truncate ${darkMode ? "text-white/50" : "text-gray-500"}`} title={log.error_message}>{log.error_message || '—'}</td>
                        <td className={`py-2 text-xs ${darkMode ? "text-white/50" : "text-gray-500"}`}>{new Date(log.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {adSyncLogsTotal > 15 && (
                <div className="flex items-center justify-between mt-3">
                  <span className={`text-xs ${darkMode ? "text-white/40" : "text-gray-400"}`}>{adSyncLogsTotal} total entries</span>
                  <div className="flex gap-2">
                    <button disabled={adSyncLogsPage <= 1} onClick={() => fetchAdSyncLogs(adSyncLogsPage - 1)}
                      className={`px-3 py-1 text-xs rounded border disabled:opacity-40 ${darkMode ? "border-white/10 text-white/60 hover:bg-white/10" : "border-gray-200 text-gray-600 hover:bg-gray-100"}`}>Prev</button>
                    <button disabled={adSyncLogsPage * 15 >= adSyncLogsTotal} onClick={() => fetchAdSyncLogs(adSyncLogsPage + 1)}
                      className={`px-3 py-1 text-xs rounded border disabled:opacity-40 ${darkMode ? "border-white/10 text-white/60 hover:bg-white/10" : "border-gray-200 text-gray-600 hover:bg-gray-100"}`}>Next</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* System Health */}
        {(activeTab as string) === "system" && (
          <div
            className={`${darkMode
                ? "bg-[#232336] border-white/10"
                : "bg-white border-gray-200"
              } rounded-lg shadow-sm p-6`}
          >
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database
                  size={20}
                  className={`${darkMode ? "text-white/80" : "text-gray-700"
                    }`}
                />
                <h3
                  className={`text-lg font-semibold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  System Health
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <div
                  className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  Last updated: {lastHealthRefresh.toLocaleTimeString()}
                </div>
                <button
                  onClick={refreshHealth}
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 px-3 py-1.5 text-white text-xs font-medium shadow"
                >
                  <RefreshCw className="h-4 w-4" /> Refresh
                </button>
              </div>
            </div>

            {[1, 2, 3, 4].map((serverIdx) => (
              <div key={serverIdx} className="mb-6">
                <div className="mb-2">
                  <h4
                    className={`text-sm font-semibold ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Server {serverIdx}
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Uptime */}
                  <div
                    className={`${darkMode ? "border-white/10" : "border-gray-200"
                      } rounded-lg border p-4`}
                  >
                    <p
                      className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      Uptime
                    </p>
                    <p
                      className={`mt-2 text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {healthData.uptimeHours}h
                    </p>
                    <p
                      className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-500"
                        }`}
                    >
                      Since last restart
                    </p>
                  </div>

                  {/* CPU */}
                  <div
                    className={`rounded-lg border p-4 ${darkMode ? "border-white/10" : "border-gray-200"
                      }`}
                  >
                    <p
                      className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      CPU Usage
                    </p>
                    <div className="mt-2">
                      <div
                        className={`${darkMode ? "bg-white/10" : "bg-gray-200"
                          } h-2 w-full rounded-full`}
                      >
                        <div
                          className={`h-2 rounded-full bg-gradient-to-r from-red-600 to-pink-600`}
                          style={{ width: `${healthData.cpuPercent}%` }}
                        />
                      </div>
                      <p
                        className={`mt-2 text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        {healthData.cpuPercent}%
                      </p>
                    </div>
                  </div>

                  {/* Memory */}
                  <div
                    className={`rounded-lg border p-4 ${darkMode ? "border-white/10" : "border-gray-200"
                      }`}
                  >
                    <p
                      className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      Memory Usage
                    </p>
                    <div className="mt-2">
                      <div
                        className={`${darkMode ? "bg-white/10" : "bg-gray-200"
                          } h-2 w-full rounded-full`}
                      >
                        <div
                          className={`h-2 rounded-full bg-gradient-to-r from-red-600 to-pink-600`}
                          style={{ width: `${healthData.memoryPercent}%` }}
                        />
                      </div>
                      <p
                        className={`mt-2 text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        {healthData.memoryPercent}%
                      </p>
                    </div>
                  </div>

                  {/* Disk */}
                  <div
                    className={`rounded-lg border p-4 ${darkMode ? "border-white/10" : "border-gray-200"
                      }`}
                  >
                    <p
                      className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      Disk Usage
                    </p>
                    <div className="mt-2">
                      <div
                        className={`${darkMode ? "bg-white/10" : "bg-gray-200"
                          } h-2 w-full rounded-full`}
                      >
                        <div
                          className={`h-2 rounded-full bg-gradient-to-r from-red-600 to-pink-600`}
                          style={{ width: `${healthData.diskPercent}%` }}
                        />
                      </div>
                      <p
                        className={`mt-2 text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        {healthData.diskPercent}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Services */}
            <div
              className={`mt-6 rounded-lg border ${darkMode ? "border-white/10" : "border-gray-200"
                }`}
            >
              <div
                className={`p-4 border-b ${darkMode ? "border-white/10" : "border-gray-200"
                  }`}
              >
                <h4
                  className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Service Status
                </h4>
              </div>
              <div
                className={`${darkMode ? "divide-white/10" : "divide-gray-200"
                  } divide-y`}
              >
                {healthData.services.map((svc, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-4"
                  >
                    <div
                      className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {svc.name}
                    </div>
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${svc.status === "healthy"
                          ? darkMode
                            ? "bg-green-500/20 text-green-400"
                            : "bg-green-100 text-green-700"
                          : svc.status === "degraded"
                            ? darkMode
                              ? "bg-yellow-500/20 text-yellow-300"
                              : "bg-yellow-100 text-yellow-700"
                            : darkMode
                              ? "bg-red-500/20 text-red-400"
                              : "bg-red-100 text-red-700"
                        }`}
                    >
                      {svc.status === "healthy"
                        ? "Healthy"
                        : svc.status === "degraded"
                          ? "Degraded"
                          : "Down"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      {/* </div> */}
      {showNotificationsModal && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm ${darkMode ? "bg-black/60" : "bg-gray-900/40"
            }`}
        >
          <div
            className={`rounded-2xl shadow-2xl p-8 md:p-12 w-[90vw] max-w-4xl relative max-h-[90vh] overflow-y-auto scrollbar-hide ${darkMode ? "bg-[#232336]" : "bg-white"
              }`}
          >
            <button
              className={`absolute top-6 right-6 transition-colors duration-200 ${darkMode
                  ? "text-white/60 hover:text-white"
                  : "text-gray-600 hover:text-gray-900"
                }`}
              onClick={() => setShowNotificationsModal(false)}
            >
              <X size={28} />
            </button>
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
                <h2
                  className={`text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Notifications
                </h2>
              </div>
            </div>
            {/* Tab Navigation */}
            <div
              className={`flex gap-1 mb-8 rounded-xl p-1 ${darkMode ? "bg-white/5" : "bg-gray-100"
                }`}
            >
              <button
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${notificationsTab === "current"
                    ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                    : darkMode
                      ? "text-white/60 hover:text-white hover:bg-white/5"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                onClick={() => setNotificationsTab("current")}
              >
                Current ({notifications.length})
              </button>
              <button
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${notificationsTab === "cleared"
                    ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                    : darkMode
                      ? "text-white/60 hover:text-white hover:bg-white/5"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                onClick={() => setNotificationsTab("cleared")}
              >
                Cleared ({clearedNotifications.length})
              </button>
            </div>
            {notificationsTab === "current" && (
              <div className="space-y-6">
                {notifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full ${darkMode ? "bg-white/5" : "bg-gray-100"
                        }`}
                    >
                      <Bell
                        size={32}
                        className={`${darkMode ? "text-white/40" : "text-gray-400"
                          }`}
                      />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      No notifications
                    </h3>
                    <p
                      className={`${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      You're all caught up! Check back later for updates.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4">
                      {notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`group rounded-xl p-6 border transition-all duration-200 ${darkMode
                              ? "bg-white/5 border-white/10 hover:border-white/20"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                            }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1">{n.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-base leading-relaxed mb-2 ${darkMode ? "text-white/90" : "text-gray-900"
                                  }`}
                              >
                                {n.text}
                              </p>
                              <div
                                className={`flex items-center gap-4 text-sm ${darkMode ? "text-white/40" : "text-gray-500"
                                  }`}
                              >
                                <span>Just now</span>
                                <span>•</span>
                                <span>Unread</span>
                              </div>
                            </div>
                            <button
                              className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0 ${darkMode
                                  ? "hover:bg-red-600/20 text-white/60 hover:text-red-400"
                                  : "hover:bg-red-50 text-gray-600 hover:text-red-600"
                                }`}
                              onClick={() => handleClearNotification(n.id)}
                              aria-label="Clear notification"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className={`flex justify-center pt-6 border-t ${darkMode ? "border-white/10" : "border-gray-200"
                        }`}
                    >
                      <button
                        className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleClearAll}
                        disabled={notifications.length === 0}
                      >
                        Clear All Notifications
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            {notificationsTab === "cleared" && (
              <div className="space-y-6">
                {clearedNotifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full ${darkMode ? "bg-white/5" : "bg-gray-100"
                        }`}
                    >
                      <Trash2
                        size={32}
                        className={`${darkMode ? "text-white/40" : "text-gray-400"
                          }`}
                      />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      No cleared notifications
                    </h3>
                    <p
                      className={`${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      Notifications you clear will appear here.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4">
                      {clearedNotifications.map((n) => (
                        <div
                          key={n.id}
                          className={`group rounded-xl p-6 border transition-all duration-200 ${darkMode
                              ? "bg-white/5 border-white/10 hover:border-white/20"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                            }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1">{n.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-base leading-relaxed mb-2 line-through ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                {n.text}
                              </p>
                              <div
                                className={`flex items-center gap-4 text-sm ${darkMode ? "text-white/30" : "text-gray-400"
                                  }`}
                              >
                                <span>Cleared</span>
                                <span>•</span>
                                <span>Just now</span>
                              </div>
                            </div>
                            <button
                              className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0 ${darkMode
                                  ? "hover:bg-green-600/20 text-white/60 hover:text-green-400"
                                  : "hover:bg-green-200 text-green-700 hover:text-green-900 bg-green-50"
                                }`}
                              onClick={() => handleRestoreNotification(n.id)}
                              aria-label="Restore notification"
                            >
                              <Plus size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPortal;

